import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonWithLoading, Label, Input, Spinner, AlertBar } from 'scorer-ui-kit';
import styled from 'styled-components';
import { BoxContainer } from '../components/BoxContainer';
import { getCameraDetails, updateFocusSettings, startReferenceCheck, updateCamera, getSnapshot } from '../apis';
import { CenterAlignedContainer, FieldContainer, SpinnerDesign, Table, TR, TH, TD, RightAlignedContainer, SelectField, LeftButton, overlayStyle, AlertContainer } from '../Style';
import refreshImg from '../images/reload.png';
import Switch from 'react-switch';
import ROISettings from './ROISettings';
import Popup from 'reactjs-popup';
import EmailTargets from './EmailTargets';
import { Gear } from '../icons/';
import PointsViewer from './PointsViewer';
import { StatusTranslate, ROI, TargetPixels, ColorsTranslate, RecordingTime } from '../constants';
import { REFERENCE_STATUS_CHECK_INTERVAL } from '../constants';
import { format } from 'date-fns';
import ReactTooltip from 'react-tooltip';


interface IRangeInput {
  isReadonly: boolean
}

interface ITableHeader {
  minWidth?: string,
  maxWidth?: string,
  width?: string,
}

const SettingsContainer = styled.div`
  margin-top: 30px;
`;

const Container = styled.div<{ editable: boolean }>`
  pointer-events: ${({ editable }) => editable ? 'default' : 'none'},
  
`;

const StyledTH = styled(TH)<ITableHeader>`
  min-width: ${({minWidth}) => minWidth};
  max-width: ${({maxWidth}) => maxWidth};
  width: ${({width}) => width};
`;

const CenteredTD = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
`;

const ResponsiveTooltip = styled.div`
  @media screen and (max-width: 2900px) {
    width: 500px;  
  }
  @media screen and (max-width: 2800px) {
    width: 460px;  
  }
  @media screen and (max-width: 2700px) {
    width: 430px;  
  }
  @media screen and (max-width: 2600px) {
    width: 400px;  
  }
  @media screen and (max-width: 2500px) {
    width: 370px;  
  }
  @media screen and (max-width: 2400px) {
    width: 340px;  
  }
  @media screen and (max-width: 2300px) {
    width: 310px;  
  }
  @media screen and (max-width: 2200px) {
    width: 280px;  
  }
  @media screen and (max-width: 2100px) {
    width: 250px;  
  }
  @media screen and (max-width: 2000px) {
    width: 220px;  
  }
  @media screen and (max-width: 1900px) {
    width: 180px;  
  }
  @media screen and (max-width: 1800px) {
    width: 160px;  
  }
  @media screen and (max-width: 1700px) {
    width: 140px;  
  }
  @media screen and (max-width: 1600px) {
    width: 108px;  
  }
  @media screen and (max-width: 1500px) {
    width: 90px;  
  }
  @media screen and (max-width: 1400px) {
    width: 80px;  
  }
`;

const LineContainer = styled.div`
  display: flex;
`;
const EmailCount = styled.div`
  width: 17px;
  height: 17px;
  border-radius: 8.5px;
  font-size: 9px;
  color: #fff;
  line-height: 17px;
  text-align: center;
  background:grey;
  position: relative;
  top: -8px;
  right: -2px;
  font-weight:900;
`;
const ReloadImage = styled.img`
  cursor: pointer;
  margin-right: 5px;
  width: 40px;
  height: 40px;
`;

const RightContainer = styled(FieldContainer)`
  flex-direction: column;
  justify-content: flex-start;
`;

const RefreshContainer = styled.div`
  display: flex;
  align-items: end;
`;

const RefreshLabel = styled(Label)`
  margin-bottom: 0px;
  cursor: pointer;
`;

const InputWrapper = styled.div`
  margin-left: 15px;
`;

const ButtonContainer = styled.div`
  margin-top: 10px;
`;

const FocusFieldContainer = styled(FieldContainer)`
    justify-content: flex-start;
    align-items: flex-start;
    margin-bottom: 10px;
`;

const FieldLabel = styled(Label)`
  padding: 5px 10px 0px 10px;
  white-space: nowrap;
`;

const PercentageContainer = styled.div`
  display:flex;
  justify-content: center;
  align-Items: baseline;
  background-color: #f7f0f0;
  width: 850px;
  margin-bottom: 30px;
`;

const RangeLabel = styled(Label)`
  padding: 0px 20px;
  margin-bottom: 0;
  white-space: nowrap;
`;

const Message = styled.div`
  margin-top: 50px;
  margin-bottom: 50px;
`;

const RangeInput = styled(Input)<IRangeInput>`
  width: 90px;
  ${({isReadonly}) => isReadonly && 'background: #ebebeb !important;'}
`;

const PercentSymbol = styled.span`
  margin-left: 6px;
`;

const ScrollContainer = styled.div<{ smallScreen: boolean }>`
  justify-content: ${({ smallScreen }) => smallScreen ? 'flex-start' : 'flex-end'};
  max-width: ${({ smallScreen }) => smallScreen ? '560px' : 'auto'};
  overflow-x: auto;
`;

const TableWrapper = styled.div`
  display: grid;
`;

const ScrollerTableContainer = styled.div`
  height: auto;
  overflow-x: auto;
  overflow-y: hidden; 
  white-space: nowrap;
  ::-webkit-scrollbar {
  width: 30px;
}
::-webkit-scrollbar-track {
  background-color: transparent;
}

::-webkit-scrollbar-thumb {
  background-color: #d6dee1;
  border-radius: 20px;
  border: 6px solid transparent;
  background-clip: content-box;
}
::-webkit-scrollbar-thumb:hover {
  background-color: gray;
}
margin-bottom:20px;
`;

const TextWithTooltip = styled.div`
  max-width: calc( 100% - 10px );
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const PreLineDiv = styled.div`
  white-space: pre-line;
`;
const DesiredColorsContainer = styled.div`
  display: flex;
  justify-content: center;
`;
const DesiredColors = styled.div`
  width: 50px;
  padding: 3px 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
  align-items: center; 
  justify-content: center;
`;
const ReferenceColor = styled.div`
  width: 100%;
  padding: 3px 4px;
  display: flex;
  flex-direction: column;
  gap: 5px 0;
  align-items: center; 
  justify-content: center;
`;
interface IColorShapeDiv {
  bgColor: string,
  colorName: string
}
const ColorShapeDiv = styled.div<IColorShapeDiv>`
  width: 10px;
  height: 10px;
  border-radius: ${({colorName})=>(colorName==='user_specified'?`10%`:`100%`)};
  background-color: ${({ bgColor }) => bgColor};
`;

interface Props {
  cameraName: string,
  device: {
    roi_lable_name: string,
    roi_lable_note1: string,
    roi_lable_note2: string
  }
}

interface Inputs {
  lights_long_short: number,
  focus: number,
  brightness_threshold: number,
  lights_on_long: number,
  auto_focus: boolean,
  movie_duration: number,
  exposure: number,
  lights_short_off: number,
  connected: boolean,
  rois: ROI[],

}

function CameraSettings({ cameraName, device }: Props) {
  const [inputs, setInputs] = useState<Inputs>({
    lights_long_short: 0,
    focus: 0,
    brightness_threshold: 0,
    lights_on_long: 0,
    auto_focus: false,
    movie_duration: 0,
    exposure: 0,
    lights_short_off: 0,
    connected: false,
    rois: []
  });
  const [focusReadOnly, setFocusReadOnly] = useState(true);
  const [settingsReadOnly, setSettingsReadOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const [referenceStatusLoading, setReferenceStatusLoading] = useState(true);
  const [alert, setAlert] = useState<{ key: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const [focusAlert, setFocusAlert] = useState<{ key: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const [imageName, setImageName] = useState('');
  const [imgMessage, setImgMessage] = useState({ key: new Date(), message: '', type: '' });
  const [imgLoading, setImgLoading] = useState(true);
  const [referenceCheckAction, setReferenceCheckAction] = useState<string>('');
  const [isPrevCallCompleted, setIsPrevCallCompleted] = useState(false);
  const [error, setError] = useState<boolean>(false);
  const [referenceAlert, setReferenceAlert] = useState<{ key: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const [cameraDetails, setCameraDetails] = useState<Inputs>();

  const { t } = useTranslation(['DetectionSettings', 'LedStatus', 'DeviceSettings', 'Common']);

  const isPrevCallCompletedRef = useRef(isPrevCallCompleted);
  isPrevCallCompletedRef.current = isPrevCallCompleted;
  const intervalRef: any = useRef();

  const getCameraInfo = useCallback(() => {
    getCameraDetails(cameraName)
      .then((res) => {
        if (res.data.status_code === '0') {
          setInputs(res.data.data);
          setCameraDetails(res.data.data);
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'info' });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
          setLoading(false);
        }
      });
  }, [cameraName]);


  useEffect(()=>{
    ReactTooltip.rebuild();
  },[inputs.rois]);

  useEffect(() => {
    if (cameraName) {
      getCameraInfo();
    }
  }, [cameraName, getCameraInfo]);

  const onInputChange = (event: any) => {
    event.persist();
    let regEx = /^[0-9][0-9]*$/;
    if (regEx.exec(event.target.value)) {
      const value = parseInt(event.target.value);
      if (Number.isInteger(value)) {
        if(event.target.name === 'focus' || event.target.name === 'exposure'){
          setInputs(prev => { 
            if(event.target.value.length > 5){
              return { ...prev }
            } else {
              return { ...prev, [event.target.name]: value }
            }
          });
        } else {
          setInputs(prev => {
            return { ...prev, [event.target.name]: value }
          });
        }
      }
    } else {
      if (event.target.value === '') {
        setInputs(prev => {
          return { ...prev, [event.target.name]: 0 }
        });
      }
    }
  };

  const onRangeInputChange = (event: any) => {
    event.persist();
    if (event.target.value === '') {
      setInputs({ ...inputs, [event.target.name]: '' });
    } else {
      setInputs({ ...inputs, [event.target.name]: event.target.value });
    }
  };

  const onChangeToggle = (checked: boolean) => {
    if (!focusReadOnly) {
      setInputs({ ...inputs, 'auto_focus': checked });
    }
  };

  const onCancel = () => {
    setFocusReadOnly(true);
    setSettingsReadOnly(true);
    getCameraInfo();
  };

  const onSaveFocusSettings = () => {
    setFocusAlert({ key: new Date(), message: '', type: undefined });
    const { auto_focus, focus, exposure } = inputs;
    let data: any = {};

    if (auto_focus !== (cameraDetails && cameraDetails.auto_focus)) {
      data.auto_focus = auto_focus;
    }

    if (focus !== (cameraDetails && cameraDetails.focus)) {
      if (!auto_focus) {
        data.focus = focus;
      }
    }

    if (exposure !== (cameraDetails && cameraDetails.exposure)) {
      data.exposure = exposure;
    }
    let jsonData: any;
    if (Object.prototype.hasOwnProperty.call(data, 'auto_focus') || Object.prototype.hasOwnProperty.call(data, 'focus') || Object.prototype.hasOwnProperty.call(data, 'exposure')) {
      jsonData = { action: 'update', data };
    } else {
      setFocusAlert({ key: new Date(), message: t('DeviceSettings:settingsUpdatedSuccessfully'), type: 'success' });
      setFocusReadOnly(true);
      return;
    }

    updateFocusSettings(cameraName, jsonData)
      .then((res) => {
        if (res.data.status_code === '0') {
          setFocusAlert({ key: new Date(), message: t('DeviceSettings:settingsUpdatedSuccessfully'), type: 'success' });
          getCameraInfo();
        } else if (res.data.status_code === '104') {
          setFocusAlert({ key: new Date(), message: t('cameraSettingsChangeIsRunning'), type: 'info' });
        } else if (res.data.status_code === '300') {
          console.error(t('LedStatus:timeoutError'));
        } else {
          setFocusAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
        setFocusReadOnly(true);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const onClickReference = () => {
    if (referenceCheckAction === 'run') {
      return;
    }
    startReferenceCheck(cameraName, 'start')
      .then((res) => {
        if (res.data.status_code === '0') {
          setReferenceCheckAction('run');
        } else if (res.data.status_code === '101') {
          setReferenceAlert({ key: new Date(), message: t('ledAnalysisIsRunning'), type: 'info' });
        } else if (res.data.status_code === '102') {
          setReferenceAlert({ key: new Date(), message: t('snapshotReloadIsRunning'), type: 'info' });
        } else if (res.data.status_code === '103') {
          setReferenceAlert({ key: new Date(), message: t('cameraSettingsChangeIsRunning'), type: 'info' });
        } else if (res.data.status_code === '104') {
          setReferenceAlert({ key: new Date(), message: t('referenceStatusCheckIsAlreadyRunning'), type: 'info' });
          setReferenceCheckAction('run');
        } else if (res.data.status_code === '300') {
          console.error(t('LedStatus:timeoutError'));
        } else {
          setReferenceAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const onSaveSettings = () => {
    setAlert({ key: new Date(), message: '', type: undefined });
    let {
      brightness_threshold,
      lights_long_short,
      lights_on_long,
      lights_short_off,
      movie_duration,
    } = inputs;

    lights_long_short = parseFloat(lights_long_short.toString());
    lights_on_long = parseFloat(lights_on_long.toString());
    lights_short_off = parseFloat(lights_short_off.toString());

    if (isNaN(lights_on_long)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidLightsOnLongValue'), type: 'error' });
      return;
    }
    if (isNaN(lights_long_short)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidLightsLongShortValue'), type: 'error' });
      return;
    }
    if (isNaN(lights_short_off)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidLightsShortOffValue'), type: 'error' });
      return;
    }

    if (!(100 >= lights_on_long && lights_on_long >= lights_long_short && lights_long_short >= lights_short_off && lights_short_off >= 0)) {
      setAlert({ key: new Date(), message: t('pleaseEnterValidRange'), type: 'error' });
      return;
    }

    const jsonData = { brightness_threshold, lights_long_short, lights_on_long, lights_short_off, movie_duration };
    updateCamera(cameraName, jsonData)
      .then((res) => {
        if (res.data.status_code === '0') {
          setSettingsReadOnly(true);
          setAlert({ key: new Date(), message: t('DeviceSettings:settingsUpdatedSuccessfully'), type: 'success' });
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
      })
      .catch((err) => {
        console.error(err);
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        }
      });
  };

  const getLatestSnapshot = useCallback(() => {
    setImgLoading(true);
    setAlert({ key: new Date(), message: '', type: undefined });
    getSnapshot(cameraName)
      .then((res) => {
        if (res.data.status_code === '0') {
          setImageName(res.data.image_name);
          setImgMessage({ key: new Date(), message: '', type: '' });
        } else if (res.data.status_code === '101') {
          setImgMessage({ key: new Date(), message: t('ledAnalysisIsRunning'), type: 'info' });
        } else if (res.data.status_code === '102') {
          setImgMessage({ key: new Date(), message: t('referenceStatusCheckIsAlreadyRunning'), type: 'info' });
        } else if (res.data.status_code === '103') {
          setImgMessage({ key: new Date(), message: t('cameraSettingsChangeIsRunning'), type: 'info' });
        } else if (res.data.status_code === '104') {
          setImgMessage({ key: new Date(), message: t('snapshotReloadIsRunning'), type: 'info' });
        } else if (res.data.status_code === '300') {
          console.error(t('LedStatus:timeoutError'));
        } else {
          setImgMessage({ key: new Date(), message: res.data.message, type: 'error' });
        }
        setImgLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setImgLoading(false);
      });
  }, [cameraName, t]);

  useEffect(() => {
    getLatestSnapshot();
  }, [getLatestSnapshot]);

  const checkReferenceStatus = useCallback(() => {
    setIsPrevCallCompleted(false);
    startReferenceCheck(cameraName, 'get_status')
      .then((res) => {
        if (res.data.status_code === '0') {
          if (res.data.status !== null) {
            setReferenceCheckAction(res.data.status);
            setError(false);
          }
        } else if (res.data.status_code === '300') {
          console.error(t('LedStatus:timeoutError'));
          setError(true);
          setReferenceCheckAction('stop');
        } else {
          setReferenceAlert({ key: new Date(), message: res.data.message, type: 'error' });
          setReferenceCheckAction('stop');
        }
        setReferenceStatusLoading(false);
        setIsPrevCallCompleted(true);
      })
      .catch((err) => {
        console.error(err);
        setReferenceStatusLoading(false);
        setIsPrevCallCompleted(true);
      });
  }, [cameraName, t]);

  useEffect(() => {
    checkReferenceStatus();
    if (error) {
      clearInterval(intervalRef.current);
    } else if (referenceCheckAction === 'run') {
      intervalRef.current = setInterval(() => {
        if (isPrevCallCompletedRef.current) {
          checkReferenceStatus();
        }
      }, REFERENCE_STATUS_CHECK_INTERVAL);
    }
    return (() => {
      clearInterval(intervalRef.current);
    });

  }, [checkReferenceStatus, error, referenceCheckAction]);

  const getButtonText = useCallback(() => {
    if (referenceCheckAction === '') {
      return '';
    } else if (referenceCheckAction === 'run') {
      return t('runningReferenceStatusCheck');
    } else {
      return t('referenceStatusCheck');
    }
  },[referenceCheckAction, t]);

  useEffect(() => {
    if (referenceCheckAction === 'stop') {
      getCameraInfo();
    }
  }, [getCameraInfo, referenceCheckAction]);

  const getConvertedReferenceDate = (date: any) => {
    return format(date, 'yyyy/MM/dd HH:mm:ss');
  };

  const generateUserSpecifiedColor = useCallback((x:number , y:number ) => {
    let median, color;

    if(x===y){
      median = x;
    } else if(x>y) {
      median = (x + (180 - x + y)/2) % 180;
    } else {
      median = (x + y)/2;
    }

    median = Math.ceil(median);
    color = `hsl(${median*2}deg,100%,50%)`;

    return color;

  },[]);

  const getRange = useCallback((colorName: string, x:number, y:number) => {
    let range;
    
      switch (colorName) {
        case 'red':
          range = '0 - 7 , 150 - 179';
          break;
    
        case 'orange':
          range = '8 - 29';
          break;
      
        case 'green':
          range = '30 - 89';
          break;
      
        case 'blue':
          range = '90 - 149';
          break;
      
        case 'user_specified':
          range = `${x} - ${y}`;
          break;

        default:
          break;
      }

    return range;

  },[]);

  const sortColors = useCallback((colors: string[])=>{
    let sortOrder = ['red', 'orange', 'green', 'blue', 'user_specified'];
    var ordering: { [key: string]: any } = {};

    for (var i=0; i<sortOrder.length; i++) {
        ordering[sortOrder[i]] = i;
    }

    colors.sort( function(a, b) {
        return (ordering[a] - ordering[b]) || a.localeCompare(b);
    });

    return colors;
  }, []);

  return (
    <SettingsContainer>
      {
        loading ?
          <Spinner size='large' styling={SpinnerDesign} /> :

          inputs &&
          <>
            {!inputs.connected &&
              <AlertContainer>
                <AlertBar message={t('cameraIsDisconnectedYouCanNotChangeSettings')} type='info' />
              </AlertContainer>}
            <Container editable={inputs.connected}>
              <ScrollContainer smallScreen={window.innerWidth < 1100}>
                <LineContainer>
                  <PointsViewer cameraName={cameraName} imageLoading={imgLoading} imageName={imageName} imageMessage={imgMessage} rois={inputs.rois} getCameraInfo={getCameraInfo} />
                  <RightContainer>
                    <RefreshContainer>
                      <ReloadImage src={refreshImg} alt={t('reloadImage')} onClick={getLatestSnapshot} title={t('reloadImage')} />
                      <RefreshLabel htmlFor='' labelText={t('refresh')} onClick={getLatestSnapshot} />
                    </RefreshContainer>
                    <BoxContainer title={t('focus')} style={{ 'width': '300px', marginTop: '20px' }}>
                      <FocusFieldContainer>
                        <Switch checked={inputs.auto_focus} onColor='#42b0f5' uncheckedIcon={false} checkedIcon={false} onChange={onChangeToggle} />
                        <FieldLabel htmlFor='' labelText={inputs.auto_focus ? t('auto') : t('manual')} />
                        {
                          !inputs.auto_focus &&
                          <InputWrapper>
                            <Input type='number' name='focus' placeholder={t('logSettingsTab.focus')} value={inputs.focus} onChange={onInputChange} style={{ marginTop: '-5px' }} disabled={focusReadOnly} />
                          </InputWrapper>
                        }
                      </FocusFieldContainer>
                      <FieldContainer>
                        <FieldLabel htmlFor='exposure' labelText={t('exposure')} />
                        <Input type='number' name='exposure' value={inputs.exposure} onChange={onInputChange} disabled={focusReadOnly} />
                      </FieldContainer>
                      <RightAlignedContainer>
                        <ButtonContainer>
                          {
                            focusReadOnly ?
                              <Button onClick={() => setFocusReadOnly(false)} size='small'>{t('Common:edit')}</Button>
                              :
                              <>
                                <LeftButton design='secondary' size='small' onClick={onCancel}>{t('Common:cancel')}</LeftButton>
                                <Button size='small' onClick={onSaveFocusSettings}>{t('Common:save')}</Button>
                              </>
                          }
                        </ButtonContainer>
                      </RightAlignedContainer>
                    </BoxContainer>
                    {
                      focusAlert.message !== '' &&
                      <AlertContainer key={focusAlert.key} style={{ width: '100%' }}>
                        <AlertBar type={focusAlert.type} message={focusAlert.message} />
                      </AlertContainer>
                    }
                  </RightContainer>
                </LineContainer>
              </ScrollContainer>

              <RightAlignedContainer>
                <ButtonWithLoading design={referenceCheckAction === 'run' ? 'danger' : 'primary'} loading={referenceStatusLoading || referenceCheckAction === 'run'} onClick={onClickReference}>{getButtonText()}</ButtonWithLoading>
              </RightAlignedContainer>
              {
                referenceAlert.message !== '' &&
                <AlertContainer key={referenceAlert.key}>
                  <AlertBar type={referenceAlert.type} message={referenceAlert.message} />
                </AlertContainer>
              }
              {
                inputs.rois.length <= 0 ?
                  <Message>{t('LedStatus:noROIAvailable')}</Message> : null
              }
              <TableWrapper>
                <ScrollerTableContainer>
                  {
                    inputs.rois.length > 0 &&
                    <Table>
                      <TR>
                        <TH bgColor='#e8e8e8' style={{ borderRight: '4px solid #d7d7d7', color: '#606060' }} colSpan={6}>{t('aboutTheLEDToBeDetected')}</TH>
                        <TH bgColor='#e2e2e2' style={{ borderRight: '4px solid #d7d7d7', color: '#606060' }} colSpan={1}>{t('showIn')}<br/>{t('cloud')}</TH>
                        <TH bgColor='#d3d3d3' style={{ borderRight: '4px solid #d7d7d7', color: '#606060' }} colSpan={3}>{t('desiredStatus')}</TH>
                        <TH bgColor='#c0c0c0' style={{ color: '#606060' }} colSpan={4}>{t('referenceStatus')}</TH>

                      </TR>
                      <TR bgColor='#f0f8ff !important'>
                        <StyledTH minWidth={'max-content'} width={'50px !important'}>{t('enabled')}/<br />{t('disabled')}</StyledTH>
                        <StyledTH minWidth={'35px'} maxWidth={'50px'}>{t('srNum')}</StyledTH>
                        <StyledTH minWidth={'87px'}>
                          <CenterAlignedContainer>
                            <ResponsiveTooltip>
                              <TextWithTooltip title={device.roi_lable_name}>{device.roi_lable_name}</TextWithTooltip>
                            </ResponsiveTooltip>
                          </CenterAlignedContainer>
                        </StyledTH>
                        <StyledTH minWidth={'87px'}>
                          <CenterAlignedContainer>
                            <ResponsiveTooltip>
                              <TextWithTooltip title={device.roi_lable_note1}>{device.roi_lable_note1}</TextWithTooltip>
                            </ResponsiveTooltip>
                          </CenterAlignedContainer>
                        </StyledTH>
                        <StyledTH minWidth={'87px'}>
                          <CenterAlignedContainer>
                            <ResponsiveTooltip>
                              <TextWithTooltip title={device.roi_lable_note2}>{device.roi_lable_note2}</TextWithTooltip>
                            </ResponsiveTooltip>
                          </CenterAlignedContainer>
                        </StyledTH>
                        <StyledTH minWidth={'max-content'} width={'50px !important'} style={{ borderRight: '4px solid #d7d7d7' }}>{t('target')}<br />{t('pixels')}</StyledTH>
                        <StyledTH minWidth={'80px'} width={'80px'} maxWidth={'80px'} style={{ borderRight: '4px solid #d7d7d7' }}>{t('enabled')}/<br />{t('disabled')}</StyledTH>
                        <StyledTH minWidth={'93px'} width={'93px'}>{t('status')}</StyledTH>
                        <StyledTH minWidth={'57px'} width={'75px'}>{t('color')}</StyledTH>
                        <StyledTH minWidth={'50px'} width={'75px'} style={{ borderRight: '4px solid #d7d7d7' }}>{t('email')}<br/>{t('address')}</StyledTH>
                        <StyledTH minWidth={'65px'} width={'75px'}>{t('color')}</StyledTH>
                        <StyledTH minWidth={'80px'} width={'90px'}>{t('brightness')}<br/>{t('minMax')}</StyledTH>
                        <StyledTH minWidth={'83px'} width={'100px'}>{t('last')}<br />{t('updateTime')}</StyledTH>
                      </TR>
                      {
                        inputs.rois.map((roi: ROI) => {
                          const { id, enabled, name, note, note2, desired_status, desired_color, desired_hue_x, desired_hue_y, reference_color, reference_hue, reference_brightness_min, reference_brightness_max, reference_last_update, pixels, email_enabled, cloud_show, target_emails_count } = roi;
                          const sortedColors = sortColors(desired_color);
                          return <TR key={id}>
                            <TD bgColor={enabled ? '#fff' : '#e8e8e8'}>{enabled ? t('enabled') : t('disabled')}</TD>
                            <TD bgColor={enabled ? '#fff' : '#e8e8e8'}>
                              <Popup
                                modal
                                trigger={<div style={{ 'color': 'blue', 'textDecoration': 'underline', 'cursor': 'pointer' }}>
                                  {id}
                                </div>}
                                {...{ overlayStyle }}
                              >
                                {(close: any) =>
                                  (<ROISettings getCameraInfo={getCameraInfo} getLatestSnapshot={getLatestSnapshot} close={close} cameraName={cameraName} device={device} roi={roi} />)}
                              </Popup>
                            </TD>
                            <TD bgColor={enabled ? '#fff' : '#e8e8e8'}>
                              <CenteredTD>
                                <ResponsiveTooltip>
                                  <TextWithTooltip title={name}>{name}</TextWithTooltip>
                                </ResponsiveTooltip>
                              </CenteredTD>
                            </TD>
                            <TD bgColor={enabled ? '#fff' : '#e8e8e8'}>
                              <CenteredTD>
                                <ResponsiveTooltip>
                                  <TextWithTooltip title={note}>{note}</TextWithTooltip>
                                </ResponsiveTooltip>
                              </CenteredTD>
                            </TD>
                            <TD bgColor={enabled ? '#fff' : '#e8e8e8'}>
                              <CenteredTD>
                                <ResponsiveTooltip>
                                  <TextWithTooltip title={note2}>{note2}</TextWithTooltip>
                                </ResponsiveTooltip>
                              </CenteredTD>
                            </TD>
                            <TD bgColor={enabled ? '#fff' : '#e8e8e8'} style={{ borderRight: '4px solid #d7d7d7' }}>{TargetPixels[pixels]}</TD>
                            <TD bgColor={cloud_show ? '#fff' : '#e8e8e8'} style={{ borderRight: '4px solid #d7d7d7' }}>{cloud_show ? t('enabled') : t('disabled')}</TD>
                            <TD bgColor={enabled ? '#fff' : '#e8e8e8'}><PreLineDiv>{t('Common:status.' + StatusTranslate[desired_status])}</PreLineDiv></TD>
                           {
                              (desired_color[0] === 'None' || desired_color[0] === 'Not Available') ?
                                <TD bgColor={enabled ? '#fff' : '#e8e8e8'}><PreLineDiv>{t('Common:color.' + ColorsTranslate[desired_color[0]])}</PreLineDiv></TD> :
                                <TD bgColor={enabled ? '#fff' : '#e8e8e8'} color={enabled ? '#fff' : '#788b91'}>
                                  <DesiredColorsContainer>
                                    <DesiredColors>
                                      {
                                        sortedColors.map((item,index)=>{
                                          return <>
                                            <ColorShapeDiv key={`${id+item+index}`} data-tip data-for={id+item+index}
                                              bgColor={`${ item==='user_specified' ? generateUserSpecifiedColor(desired_hue_x!,desired_hue_y!) : item }`}
                                              colorName={item}
                                            />
                                            <ReactTooltip id={id+item+index}>
                                              <div>
                                                {t('Common:color.' + ColorsTranslate[item])}
                                                {` (${getRange(item, desired_hue_x!, desired_hue_y!)})`}
                                              </div>
                                            </ReactTooltip>
                                          </>
                                        })
                                      }
                                    </DesiredColors>
                                  </DesiredColorsContainer>
                                </TD>
                            }
                            <TD bgColor={enabled ? '#fff' : '#e8e8e8'} style={{ borderRight: '4px solid #d7d7d7' }}>
                              <Popup
                                modal
                                trigger={
                                  <CenterAlignedContainer>

                                    <Gear size={24} color={email_enabled ? '#6dc3ed' : '#788b91'} style={{ cursor: 'pointer' }} />
                                    <EmailCount>{target_emails_count}</EmailCount>
                                  </CenterAlignedContainer>
                                }
                                {...{ overlayStyle }}
                              >
                                {(close: any) =>
                                  (<EmailTargets getCameraInfo={getCameraInfo} close={close} cameraName={cameraName} roi={roi} />)}
                              </Popup>
                            </TD>
                            {enabled ? <>
                              {
                                (reference_color === 'None' || reference_color === 'Not Available') ?
                                  <TD bgColor={enabled ? '#fff' : '#e8e8e8'}>{t('Common:color.' + ColorsTranslate[reference_color])}</TD> :
                                  <TD bgColor={enabled ? '#fff' : '#e8e8e8'}>
                                    <ReferenceColor>
                                      <ColorShapeDiv
                                        bgColor={
                                          `${ reference_color==='user_specified' ? 
                                            ( reference_hue!==null ? `hsl(${reference_hue*2}deg,100%,50%)` : 'transparent' ) 
                                          : reference_color }` }
                                        colorName={reference_color}
                                      />
                                      {t('Common:color.' + ColorsTranslate[reference_color])} <br />
                                      {reference_hue!==null && `(${reference_hue})`}
                                    </ReferenceColor>
                                  </TD>
                              }
                              <TD bgColor={enabled ? '#fff' : '#e8e8e8'} >{reference_brightness_min}/{reference_brightness_max}</TD>
                              <TD bgColor={enabled ? '#fff' : '#e8e8e8'}>
                                {
                                  getConvertedReferenceDate(new Date(reference_last_update)).split(' ').map((values:any,index) => {
                                    return(
                                      <div key={index}>
                                        {values}
                                      </div>
                                    )
                                  })
                                }
                              </TD>
                            </> :
                              <>
                                <TD bgColor='#e8e8e8'>NA</TD>
                                <TD bgColor='#e8e8e8'>NA</TD>
                                <TD bgColor='#e8e8e8'>NA</TD>
                              </>}
                          </TR>;
                        }

                        )
                      }
                    </Table>
                  }
                </ScrollerTableContainer>
              </TableWrapper>

              <ScrollContainer smallScreen={window.innerWidth < 1275}>
                <BoxContainer title=''>
                  <FocusFieldContainer>
                    <FieldLabel htmlFor='movie_duration' style={{ width: '190px', marginTop: '6px' }} labelText={t('recordingTime')} />
                    <SelectField style={{ marginLeft: '20px' }} name='movie_duration' defaultValue={inputs.movie_duration} onChange={onInputChange} disabled={settingsReadOnly} value={inputs.movie_duration}>
                      {
                        RecordingTime.map((time, index) => <option key={index} value={time}>{time}</option>)
                      }
                    </SelectField>
                  </FocusFieldContainer>
                  <FocusFieldContainer>
                    <FieldLabel htmlFor='brightness_threshold' style={{ marginTop: '6px', width: '210px' }} labelText={t('judgementBrightnessValue')} />
                    <Input type='number' name='brightness_threshold' placeholder={t('logSettingsTab.enterDays')} style={{ width: '100px' }} value={inputs.brightness_threshold} onChange={onInputChange} min={0} max={255} defaultValue='1' disabled={settingsReadOnly} />
                  </FocusFieldContainer>
                  <PercentageContainer>
                    <RangeInput isReadonly={true} defaultValue='100' disabled /><PercentSymbol>%</PercentSymbol>
                    <RangeLabel htmlFor='' labelText={t('on')} />
                    <RangeInput isReadonly={false} type='number' step='00.01' max={100} name='lights_on_long' value={inputs.lights_on_long} onChange={onRangeInputChange} defaultValue='90' disabled={settingsReadOnly} /><PercentSymbol>%</PercentSymbol>
                    <RangeLabel htmlFor='' labelText={t('longBlink')} />
                    <RangeInput isReadonly={false} type='number' step='00.01' max={100} name='lights_long_short' value={inputs.lights_long_short} onChange={onRangeInputChange} defaultValue='40' disabled={settingsReadOnly} /><PercentSymbol>%</PercentSymbol>
                    <RangeLabel htmlFor='' labelText={t('shortBlink')} />
                    <RangeInput isReadonly={false} type='number' step='00.01' max={100} name='lights_short_off' value={inputs.lights_short_off} onChange={onRangeInputChange} defaultValue='10' disabled={settingsReadOnly} /><PercentSymbol>%</PercentSymbol>
                    <RangeLabel htmlFor='' labelText={t('off')} />
                    <RangeInput isReadonly={true} defaultValue='0'disabled /><PercentSymbol>%</PercentSymbol>
                  </PercentageContainer>
                  <CenterAlignedContainer>
                    <ButtonContainer>
                      {
                        settingsReadOnly ?
                          <Button onClick={() => setSettingsReadOnly(false)}>{t('Common:edit')}</Button>
                          :
                          <>
                            <LeftButton design='secondary' onClick={onCancel}>{t('Common:cancel')}</LeftButton>
                            <Button onClick={onSaveSettings}>{t('Common:save')}</Button>
                          </>
                      }
                    </ButtonContainer>
                  </CenterAlignedContainer>
                  {
                    alert.message !== '' &&
                    <AlertContainer key={alert.key}>
                      <AlertBar type={alert.type} message={alert.message} />
                    </AlertContainer>
                  }
                </BoxContainer>
              </ScrollContainer>
            </Container>
          </>
      }
    </SettingsContainer>
  );
}

export default CameraSettings;
